import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import qs from "qs";
import { pingIntervalAtom, smetaStore } from "../atoms";
import { Prefixes } from "./MainPage/Tools/ProjectX";
import { renderMessageBox, setLoadingCursorAxios } from "./TokenProcessing/TokenProcessing";
import { createPingInterval } from "./Url";

declare module "axios" {
  export interface AxiosRequestConfig {
    prefix?: Prefixes;
    loadMask?: boolean | HTMLDivElement;
  }
}

const checkServerAnswer = (json: AxiosResponse) => {
  if (json.data?.Message?.content) {
    renderMessageBox({
      dlgType: 2,
      buttons: 1,
      message: json.data.Message.content,
    });

    delete json.data.Message;
  }
  if (json.data?.error && !json.data?.error?.NeedNewPW && !json.data?.error?.LicRes) {
    renderMessageBox({ dlgType: 1, buttons: 1, message: json.data.error.content });
    delete json.data.error;
  }
  return json;
};

const baseUrl = window.location.port === "3000" ? "http://localhost:1317/web~" : window.location.origin + "/web~";

export const customAxios = axios.create({
  baseURL: baseUrl,
  paramsSerializer: (params) => {
    const encodeParams = Object.entries(params).reduce((acc: { [key: string]: any }, [key, value]) => {
      if (typeof value === "string") {
        acc[key] = value
          // .replaceAll(";", "%3B")
          // .replaceAll(",", "%2C")
          .replaceAll("/", "%2F");
        // .replaceAll("&", "%3F")
        // .replaceAll("@", "%40")
        // .replaceAll("&", "%26")
        // .replaceAll("=", "%3D")
        // .replaceAll("+", "%2B")
        // .replaceAll("$", "%24")
        // .replaceAll("#", "%23");
      } else acc[key] = value;
      return acc;
    }, {});

    return qs.stringify(encodeParams, { encode: false }).replaceAll(/ /g, "%20");
  },
});

customAxios.interceptors.request.use(
  function (config) {
    if (config.baseURL) config.baseURL += config.prefix || Prefixes.default;
    if (!config.params?.LicGUID) {
      const licGuid = sessionStorage.getItem("LicG"); //сессия из хранилища
      config.params = { ...config.params, LicGUID: licGuid };
    }
    if (config.loadMask) config.loadMask = setLoadingCursorAxios(true);
    document.body.setAttribute("data-locked", "true");
    return config;
  },
  function (error) {
    document.body.setAttribute("data-locked", "false");
    const config = error.config;
    if (config && config.loadMask) config.loadMask.remove();
    console.log(error);
    return Promise.reject(error);
  },
);

customAxios.interceptors.response.use(
  function (response) {
    document.body.setAttribute("data-locked", "false");
    const config = response.config;
    const checkedResp = checkServerAnswer(response);
    const pingInterval = smetaStore.get(pingIntervalAtom);
    if (pingInterval) clearInterval(pingInterval);
    smetaStore.set(pingIntervalAtom, createPingInterval);
    if (config && config.loadMask) config.loadMask.remove();
    return checkedResp;
  },
  function (error) {
    document.body.setAttribute("data-locked", "false");
    const config = error.config;
    if (config && config.loadMask) config.loadMask.remove();
    return Promise.reject(error);
  },
);

interface PromiseControl<T> {
  promise: Promise<T>;
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason?: any) => void;
}

type PromiseControlArray<T> = PromiseControl<T>[];

class API {
  static current_queue: PromiseControlArray<any> = [];
  static pendingPromise = false;
  static http = customAxios;
  static retryTimeout = null;
  static workingOnPromise = false;

  static queue<T = unknown>(promise): Promise<T> {
    return new Promise((resolve, reject) => {
      API.current_queue.push({
        promise,
        resolve,
        reject,
      });
      API.dequeue();
    });
  }

  static retry() {
    clearTimeout(API.retryTimeout);
    console.log("Пробую переподключится к интернету!");
    API.retryTimeout = setTimeout(() => API.dequeue(), 5000);
  }

  static afterPromise = (item, resolve = false, data) => {
    this.workingOnPromise = false;
    if (resolve) item.resolve(data);
    else item.reject(data);
    API.dequeue();
  };

  static dequeue() {
    if (this.workingOnPromise) {
      return false;
    }
    if (window.navigator && !window.navigator.onLine) {
      return API.retry();
    }
    const item = API.current_queue.shift();
    if (!item) {
      return false;
    }
    this.workingOnPromise = true;
    item
      .promise()
      .then((value: unknown) => API.afterPromise(item, true, value))
      .catch((error: AxiosError) => API.afterPromise(item, false, error));
    return true;
  }

  static debug() {
    return new Promise((res) => setTimeout(() => res("Completed"), 2000));
  }

  static async request(config: AxiosRequestConfig) {
    return this.http(config)
      .then((response) => response.data)
      .catch((error) => Promise.reject(error));
  }
}

new API();

const smetaAxios = <T = unknown>(config: AxiosRequestConfig): Promise<T> => {
  return API.queue(() => API.request({ ...config, data: JSON.stringify(config.data || {}) }));
};

smetaAxios.get = <T = unknown>(url = "", config?: AxiosRequestConfig): Promise<T> => {
  return API.queue(() => API.request({ method: "get", url, ...config }));
};

smetaAxios.post = <T = unknown>(url = "", data = {}, config?: AxiosRequestConfig): Promise<T> => {
  return API.queue(() => API.request({ method: "post", url, data: JSON.stringify(data), ...config }));
};

export default smetaAxios;
